import { z } from 'zod';
import { CA_PROVINCES, US_STATES } from '../types/incentives.js';
export const SERVED_REGION_COUNTRIES = [
    { country: 'US', label: 'United States' },
    { country: 'CA', label: 'Canada' },
];
export const SERVED_REGIONS = [
    ...US_STATES.map((s) => ({ code: s.code, name: s.name, country: 'US' })),
    ...CA_PROVINCES.map((p) => ({ code: p.code, name: p.name, country: 'CA' })),
];
export const SERVED_REGION_CODES = SERVED_REGIONS.map((r) => r.code);
const ORDER = new Map(SERVED_REGION_CODES.map((code, i) => [code, i]));
const BY_CODE = new Map(SERVED_REGIONS.map((r) => [r.code, r]));
export const MAX_SERVED_REGIONS = SERVED_REGIONS.length;
export function isServedRegionCode(value) {
    return typeof value === 'string' && ORDER.has(value);
}
export function servedRegion(code) {
    return BY_CODE.get(code) ?? null;
}
/** "Ontario" for ON; an unknown value is shown as it is. */
export function servedRegionName(code) {
    return BY_CODE.get(code)?.name ?? code;
}
/**
 * The stored form of a list of codes: trimmed, upper case, each once, in
 * list order. Null when any entry is not a code from the list, so a caller
 * refuses the input rather than dropping part of it silently.
 */
export function normalizeServedRegions(input) {
    if (!Array.isArray(input) || input.length > MAX_SERVED_REGIONS * 2)
        return null;
    const out = new Set();
    for (const raw of input) {
        if (typeof raw !== 'string')
            return null;
        const code = raw.trim().toUpperCase();
        if (!ORDER.has(code))
            return null;
        out.add(code);
    }
    return [...out].sort((a, b) => ORDER.get(a) - ORDER.get(b));
}
/** The codes of a stored value that are still on the list, in list order.
 *  For reads of records written before validation existed. */
export function knownServedRegions(input) {
    if (!Array.isArray(input))
        return [];
    return normalizeServedRegions(input.filter((v) => typeof v === 'string' && ORDER.has(v.trim().toUpperCase()))) ?? [];
}
/** Request body field: an array of codes from the list (empty clears). */
export const ServedRegionsSchema = z.array(z.string().max(8)).max(MAX_SERVED_REGIONS * 2).transform((value, ctx) => {
    const out = normalizeServedRegions(value);
    if (!out) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Regions must be US state or Canadian province codes, such as ON or CA.' });
        return z.NEVER;
    }
    return out;
});
/** "Ontario, Quebec" (or the codes, when short is asked for). */
export function servedRegionsLabel(codes, opts = {}) {
    const list = knownServedRegions(codes ?? []);
    if (!list.length)
        return 'None recorded';
    return list.map((c) => (opts.short ? c : servedRegionName(c))).join(', ');
}
/** True when the two lists share a code. */
export function servedRegionsOverlap(a, b) {
    if (!a?.length || !b?.length)
        return false;
    const set = new Set(a);
    return b.some((c) => set.has(c));
}
/**
 * The SP Manager to suggest for a new partner (Warren, 25 October 2026:
 * "suggest the SP Manager whose territory covers their region (Super Admin
 * confirms)"). The rule: for each manager, count the applicant's regions
 * that appear in the manager's territory; suggest every manager with the
 * highest count, provided it is at least one. Ties are all listed, by name
 * then id. No regions on the application, or no manager covering any of
 * them, means no suggestion. The caller passes only managers who may take a
 * partner today; this function never assigns anyone.
 */
export function suggestSpManagers(applicantRegions, managers) {
    const wanted = knownServedRegions(applicantRegions ?? []);
    const scored = managers.map((m) => {
        const territory = new Set(knownServedRegions(m.served_regions));
        return { staff_id: m.staff_id, name: m.name, covered: wanted.filter((c) => territory.has(c)) };
    });
    const best = scored.reduce((n, m) => Math.max(n, m.covered.length), 0);
    const candidates = best === 0 ? [] : scored.filter((m) => m.covered.length === best)
        .sort((a, b) => a.name.localeCompare(b.name) || a.staff_id.localeCompare(b.staff_id));
    return { covered: best, of: wanted.length, candidates };
}
//# sourceMappingURL=servedRegions.js.map